import { useCallback, useEffect, useRef } from 'react';
import { UseFieldArrayReturn, UseFormReturn, useWatch } from 'react-hook-form';
import { IdentificationFormData, PoliticallyExposedPerson } from '../types';

/**
 * Debounce delay in milliseconds for form change callbacks.
 * This prevents excessive re-renders and state updates while typing.
 */
const DEBOUNCE_DELAY = 300;

/**
 * Fields that affect tab visibility and require parent notification.
 * Only changes to these fields will trigger the onChange callback.
 */
const TAB_RELEVANT_FIELDS = [
  'country',
  'legalForm',
  'isFinancialIntermediary',
  'listedCompany',
  'subsidiaryOwnership',
] as const;

interface UseFormEffectsProps {
  form: UseFormReturn<IdentificationFormData>;
  conditionalVisibility: {
    showFinancialIntermediaryFields: boolean;
    showListedCompanyFields: boolean;
    showParentSubsidiaryFields: boolean;
    showCorporateFields: boolean;
    showPepFields: boolean;
  };
  sameAddressAsContractingParty?: boolean;
  pepFieldArray: UseFieldArrayReturn<
    IdentificationFormData,
    'politicallyExposedPersons',
    'id'
  >;
  defaultPepValues: PoliticallyExposedPerson;
  onChange?: (data: Partial<IdentificationFormData>) => void;
}

/**
 * Custom hook to manage form side effects (field clearing, value copying, etc.)
 */
export const useFormEffects = ({
  form,
  conditionalVisibility,
  sameAddressAsContractingParty,
  pepFieldArray,
  defaultPepValues,
  onChange,
}: UseFormEffectsProps) => {
  const {
    showFinancialIntermediaryFields,
    showListedCompanyFields,
    showParentSubsidiaryFields,
    showCorporateFields,
    showPepFields,
  } = conditionalVisibility;

  // Track if we've already added initial PEP to prevent infinite loops
  const pepInitializedRef = useRef(false);

  // Clear financial intermediary fields when condition is not met
  useEffect(() => {
    if (!showFinancialIntermediaryFields) {
      form.setValue('financialIntermediaryType', '');
      form.setValue('financialIntermediaryTypeOther', '');
      form.setValue('supervisoryOrganization', '');
    }
  }, [showFinancialIntermediaryFields, form]);

  // Clear financialIntermediaryTypeOther when financialIntermediaryType is not 'other'
  const financialIntermediaryType = useWatch({
    control: form.control,
    name: 'financialIntermediaryType',
  });

  useEffect(() => {
    if (financialIntermediaryType && financialIntermediaryType !== 'other') {
      form.setValue('financialIntermediaryTypeOther', '');
    }
  }, [financialIntermediaryType, form]);

  // Clear listed company fields when condition is not met
  useEffect(() => {
    if (!showListedCompanyFields) {
      form.setValue('stockExchange', '');
      form.setValue('tickerSymbol', '');
    }
  }, [showListedCompanyFields, form]);

  // Clear parent/subsidiary fields when condition is not met
  useEffect(() => {
    if (!showParentSubsidiaryFields) {
      form.setValue('parentCompanyExchange', '');
      form.setValue('parentTicker', '');
    }
  }, [showParentSubsidiaryFields, form]);

  // Clear corporate fields when legal form doesn't support them
  useEffect(() => {
    if (!showCorporateFields) {
      form.setValue('listedCompany', '');
      form.setValue('subsidiaryOwnership', '');
    }
  }, [showCorporateFields, form]);

  // Clear PEP fields when condition is not met, or add one empty PEP when "Yes" is selected
  useEffect(() => {
    if (!showPepFields) {
      form.setValue('politicallyExposedPersons', []);
      pepInitializedRef.current = false;
    } else {
      // If "Yes" is selected and no PEP entries exist, add one automatically
      const currentPeps = form.getValues('politicallyExposedPersons');
      if (
        !pepInitializedRef.current &&
        (!currentPeps || currentPeps.length === 0)
      ) {
        pepFieldArray.append(defaultPepValues);
        pepInitializedRef.current = true;
      }
    }
    // Remove pepFieldArray from dependencies to prevent infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPepFields, form, defaultPepValues]);

  // Clear legal form when switching between Switzerland and other countries
  // because dropdown values are not valid for text input and vice versa
  const country = useWatch({
    control: form.control,
    name: 'country',
  });

  const prevCountryRef = useRef(country);

  useEffect(() => {
    // Only clear if country actually changed (not initial render)
    if (prevCountryRef.current !== country) {
      const currentLegalForm = form.getValues('legalForm');
      if (currentLegalForm) {
        form.setValue('legalForm', '');
      }
      form.setValue('legalFormOther', '');
    }
    prevCountryRef.current = country;
  }, [country, form]);

  const legalForm = useWatch({
    control: form.control,
    name: 'legalForm',
  });

  useEffect(() => {
    if (legalForm !== 'other') {
      form.setValue('legalFormOther', '');
    }
  }, [legalForm, form]);

  // Track previous value to detect actual user changes vs initial state
  const previousSameAddress = useRef(sameAddressAsContractingParty);

  // Copy address from point 1 to point 1.3 when checkbox is checked
  // Clear operational office fields when checkbox is unchecked
  useEffect(() => {
    // Only proceed if the value has actually changed (not just initial render)
    const hasChanged =
      previousSameAddress.current !== sameAddressAsContractingParty;
    previousSameAddress.current = sameAddressAsContractingParty;

    if (!hasChanged) {
      // Skip on initial render to preserve pre-filled data
      return;
    }

    if (sameAddressAsContractingParty) {
      // Copy data from point 1 (head office) to point 1.3 (operational office)
      const { companyName, headOfficeAddress, postcode, city, country } =
        form.getValues();

      form.setValue('operationalOfficeCompanyName', companyName);
      form.setValue('operationalOfficeAddress', headOfficeAddress);
      form.setValue('operationalOfficePostcode', postcode);
      form.setValue('operationalOfficeCity', city);
      form.setValue('operationalOfficeCountry', country);
    } else {
      // Clear operational office fields when checkbox is explicitly unchecked by user
      form.setValue('operationalOfficeCompanyName', '');
      form.setValue('operationalOfficeAddress', '');
      form.setValue('operationalOfficePostcode', '');
      form.setValue('operationalOfficeCity', '');
      form.setValue('operationalOfficeCountry', '');
    }
  }, [sameAddressAsContractingParty, form]);

  // Debounce timer ref for onChange callback
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Memoized onChange handler to prevent unnecessary closures
  const handleDebouncedChange = useCallback(
    (data: IdentificationFormData) => {
      if (!onChange) return;
      onChange(data);
    },
    [onChange]
  );

  // Real-time form changes for tab visibility - OPTIMIZED with debouncing
  // Only triggers onChange when tab-relevant fields change
  useEffect(() => {
    if (!onChange) return;

    const subscription = form.watch((data, { name }) => {
      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Only notify parent for tab-relevant field changes
      // Skip if name is undefined (initial watch) or not a tab-relevant field
      const isTabRelevantChange =
        name === undefined ||
        TAB_RELEVANT_FIELDS.includes(
          name as (typeof TAB_RELEVANT_FIELDS)[number]
        );

      if (!isTabRelevantChange) {
        // For non-tab-relevant changes, still update parent but with longer debounce
        // This ensures data persistence without affecting performance
        debounceTimerRef.current = setTimeout(() => {
          handleDebouncedChange(data as IdentificationFormData);
        }, DEBOUNCE_DELAY);
        return;
      }

      // For tab-relevant changes, use a shorter debounce to keep UI responsive
      debounceTimerRef.current = setTimeout(() => {
        handleDebouncedChange(data as IdentificationFormData);
      }, 100);
    });

    return () => {
      subscription.unsubscribe();
      // Clear debounce timer on cleanup
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [form, onChange, handleDebouncedChange]);
};
